import React, { useEffect, useRef, useState } from 'react';
import { ApiError, type AdminApiClient } from './api-client';
import { ActionButton, ConfirmDialog, Notice } from './admin-feedback';
import {
  organizationEffectiveActive,
  commonFieldsForGrantedFunctions,
  canonicalFieldRules,
  organizationTree,
  readBusinessWorkspace,
  securityRecord,
  type BusinessSecurityWorkspace as Workspace,
  type SecurityUser,
  type PersonalAccess,
} from './business-security-model';
import { PermissionScopeEditor, PermissionFieldSettings } from './permission-scope-editor';
import { UserPermissionCheck } from './user-permission-check';
import './employee-workspace.css';

const BASE = '/api/admin/m8/security';
const WORKSPACE = `${BASE}/workspace`;
const LIST = '/admin/security/users';
const PAGE_SIZE = 15;
type View = { mode: 'list' | 'new' | 'edit'; userId?: string | undefined };
type PasswordDialog = { user: SecurityUser; reset: boolean };
const sameIds = (a: string[], b: string[]) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
const cloneAccess = (access: PersonalAccess | null | undefined): PersonalAccess | null =>
  access
    ? {
        ...structuredClone(access),
        ...(access.fieldPolicies
          ? { fieldPolicies: canonicalFieldRules(access.fieldPolicies) }
          : {}),
      }
    : null;
const accessEqual = (
  a: PersonalAccess | null | undefined,
  b: PersonalAccess | null | undefined,
) => {
  const normalize = (access: PersonalAccess | null | undefined) =>
    access
      ? {
          permissions: access.permissions
            .map((rule) => `${rule.permissionId}:${rule.effect}`)
            .sort(),
          businessScopeGroups: access.businessScopeGroups
            .map((group) => ({
              appId: group.appId,
              cityIds: group.cityIds?.slice().sort() ?? null,
              productIds: group.productIds?.slice().sort() ?? null,
              orderIds: group.orderIds?.slice().sort() ?? null,
              includeCentralOrders: group.includeCentralOrders === true,
            }))
            .sort((left, right) => left.appId.localeCompare(right.appId)),
          fieldPolicies: canonicalFieldRules(access.fieldPolicies ?? [])
            .map(
              (rule) =>
                `${rule.permissionId}:${rule.fieldName}:${rule.classification}:${rule.access}`,
            )
            .sort(),
        }
      : null;
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
};

function safeError(error: unknown, fallback: string, secret = '') {
  if (error instanceof ApiError && error.status === 403)
    return '当前账号没有此操作权限，请联系平台管理员。';
  if (error instanceof ApiError && error.status === 409)
    return '员工资料已发生变化，请重新加载确认后再操作。';
  return error instanceof Error &&
    /[\u3400-\u9fff]/u.test(error.message) &&
    !(secret && error.message.includes(secret))
    ? error.message
    : fallback;
}

async function requestWithin<T>(
  api: AdminApiClient,
  path: string,
  init: RequestInit,
  signal: AbortSignal,
) {
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let cancel: (() => void) | undefined;
  const cancelled = new Promise<never>((_, reject) => {
    cancel = () => {
      controller.abort();
      reject(new Error('Request cancelled'));
    };
  });
  const abort = () => cancel?.();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  const deadline = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      controller.abort();
      reject(new Error('请求超时，请重新加载确认后再试。'));
    }, 15_000);
  });
  try {
    return await Promise.race([
      api.request<T>(path, { ...init, cache: 'no-store', signal: controller.signal }),
      cancelled,
      deadline,
    ]);
  } finally {
    if (timeout) clearTimeout(timeout);
    signal.removeEventListener('abort', abort);
  }
}

function passwordProblem(password: string, confirmation: string) {
  if (password.length < 12 || password.length > 128) return '密码需为 12–128 个字符。';
  if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password))
    return '密码须同时包含英文大写字母、小写字母和数字，可使用符号。';
  if (password !== confirmation) return '两次输入的密码不一致，请重新确认。';
  return '';
}

export interface EmployeeWorkspaceProps {
  api: AdminApiClient;
  staffMode?: 'new' | 'edit' | undefined;
  staffUserId?: string | undefined;
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
}

export function EmployeeWorkspace({
  api,
  staffMode,
  staffUserId,
  onDirtyChange,
}: EmployeeWorkspaceProps) {
  const [view, setView] = useState<View>({ mode: staffMode ?? 'list', userId: staffUserId });
  const [data, setData] = useState<Workspace>();
  const [loading, setLoading] = useState(true),
    [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false),
    [feedback, setFeedback] = useState('');
  const [search, setSearch] = useState(''),
    [statusFilter, setStatusFilter] = useState(''),
    [organizationFilter, setOrganizationFilter] = useState(''),
    [page, setPage] = useState(1);
  const [name, setName] = useState(''),
    [username, setUsername] = useState(''),
    [organizationId, setOrganizationId] = useState(''),
    [roleIds, setRoleIds] = useState<string[]>([]);
  const [personalAccess, setPersonalAccess] = useState<PersonalAccess | null>(null);
  const [formError, setFormError] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [passwordDialog, setPasswordDialog] = useState<PasswordDialog>();
  const [password, setPassword] = useState(''),
    [passwordConfirmation, setPasswordConfirmation] = useState(''),
    [passwordError, setPasswordError] = useState('');
  const [confirmation, setConfirmation] = useState<{
    title: string;
    description: React.ReactNode;
    action: () => void;
  }>();
  const [leave, setLeave] = useState<() => void>();
  const generation = useRef(0),
    running = useRef(false),
    operation = useRef<AbortController>();
  const dataRef = useRef<Workspace>();
  const navigationBypass = useRef(false);
  const initialized = useRef('');
  const current = data?.users.find((item) => item.id === view.userId);
  const self = Boolean(current && current.id === data?.currentUser.id);
  const dirty =
    view.mode === 'new'
      ? Boolean(name || username || organizationId)
      : view.mode === 'edit' &&
        Boolean(
          current &&
          (name.trim() !== current.displayName ||
            organizationId !== (current.organizationId ?? '') ||
            !sameIds(roleIds, current.roleIds) ||
            !accessEqual(personalAccess, current.personalAccess)),
        );
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const locked = busy || loading || Boolean(loadError);
  const canManage = data?.currentUser.canManage === true;
  const blocked = locked || !canManage;
  const editBlocked = blocked || self;

  function clearPasswords() {
    setPassword('');
    setPasswordConfirmation('');
    setPasswordError('');
  }
  function closePassword() {
    if (running.current) return;
    clearPasswords();
    setPasswordDialog(undefined);
  }
  function commitView(next: View, replace = false) {
    clearPasswords();
    setPasswordDialog(undefined);
    setConfirmation(undefined);
    setLeave(undefined);
    setFormError('');
    initialized.current = '';
    setView(next);
    const path =
      next.mode === 'new'
        ? `${LIST}/new`
        : next.mode === 'edit'
          ? `${LIST}/${encodeURIComponent(next.userId ?? '')}/edit`
          : LIST;
    if (window.location.pathname !== path) {
      window.history[replace ? 'replaceState' : 'pushState']({}, '', path);
      navigationBypass.current = true;
      try {
        window.dispatchEvent(new PopStateEvent('popstate'));
      } finally {
        navigationBypass.current = false;
      }
    }
  }
  function navigate(next: View) {
    if (running.current || loading) {
      setFeedback('正在确认员工资料，请等待当前操作完成后再离开。');
      return;
    }
    if (dirtyRef.current) setLeave(() => () => commitView(next));
    else commitView(next);
  }
  async function read(signal: AbortSignal, id: number) {
    const next = readBusinessWorkspace(await requestWithin(api, WORKSPACE, {}, signal));
    if (signal.aborted || generation.current !== id) throw new Error('Request cancelled');
    dataRef.current = next;
    setData(next);
    return next;
  }
  useEffect(() => {
    const controller = new AbortController(),
      id = ++generation.current;
    operation.current?.abort();
    operation.current = controller;
    running.current = false;
    dataRef.current = undefined;
    initialized.current = '';
    setData(undefined);
    setBusy(false);
    setLoading(true);
    setLoadError('');
    setFeedback('');
    setName('');
    setUsername('');
    setOrganizationId('');
    setRoleIds([]);
    setPersonalAccess(null);
    clearPasswords();
    setPasswordDialog(undefined);
    setConfirmation(undefined);
    setLeave(undefined);
    void read(controller.signal, id)
      .catch((error) => {
        if (!controller.signal.aborted && generation.current === id)
          setLoadError(safeError(error, '员工资料读取失败，请重试读取。'));
      })
      .finally(() => {
        if (!controller.signal.aborted && generation.current === id) setLoading(false);
      });
    return () => {
      controller.abort();
      operation.current?.abort();
    };
  }, [api]);
  useEffect(() => {
    setView({ mode: staffMode ?? 'list', userId: staffUserId });
    initialized.current = '';
    clearPasswords();
    setPasswordDialog(undefined);
    setConfirmation(undefined);
    setLeave(undefined);
  }, [staffMode, staffUserId]);
  useEffect(() => {
    const key = `${view.mode}:${view.userId ?? ''}`;
    if (!data || initialized.current === key) return;
    if (view.mode === 'edit' && !current) return;
    initialized.current = key;
    setName(current?.displayName ?? '');
    setUsername(current?.username ?? '');
    setOrganizationId(current?.organizationId ?? '');
    setRoleIds(current?.roleIds.slice() ?? []);
    setPersonalAccess(cloneAccess(current?.personalAccess));
  }, [data, current, view.mode, view.userId]);
  useEffect(() => {
    onDirtyChange?.(dirty || Boolean(password));
    return () => onDirtyChange?.(false);
  }, [dirty, password, onDirtyChange]);
  useEffect(() => {
    const routeNavigation = (event: Event) => {
      if (!(event instanceof CustomEvent) || navigationBypass.current) return;
      const detail = securityRecord(event.detail);
      if (
        typeof detail.commit !== 'function' ||
        (!dirtyRef.current && !password && !running.current)
      )
        return;
      event.preventDefault();
      if (running.current || loading) {
        setFeedback('正在确认员工资料，请等待当前操作完成后再离开。');
        return;
      }
      clearPasswords();
      setPasswordDialog(undefined);
      const commit = detail.commit as () => void;
      setLeave(() => () => commit());
    };
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current && !password && !running.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const click = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest<HTMLAnchorElement>('a[href]');
      if (
        !link ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0 ||
        link.target === '_blank' ||
        link.hasAttribute('download')
      )
        return;
      const target = new URL(link.href, window.location.href);
      if (target.href === window.location.href || target.origin !== window.location.origin) return;
      if (
        target.pathname === window.location.pathname &&
        target.search === window.location.search &&
        target.hash
      )
        return;
      if (!dirtyRef.current && !password && !running.current) return;
      event.preventDefault();
      event.stopPropagation();
      if (running.current || loading) {
        setFeedback('正在确认员工资料，请等待当前操作完成后再离开。');
        return;
      }
      clearPasswords();
      setPasswordDialog(undefined);
      setLeave(() => () => {
        window.history.pushState({}, '', target.href);
        navigationBypass.current = true;
        try {
          window.dispatchEvent(new PopStateEvent('popstate'));
        } finally {
          navigationBypass.current = false;
        }
      });
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && passwordDialog && !running.current) closePassword();
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('travel:admin-navigation', routeNavigation);
    document.addEventListener('click', click, true);
    window.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('travel:admin-navigation', routeNavigation);
      document.removeEventListener('click', click, true);
      window.removeEventListener('keydown', escape);
    };
  }, [password, passwordDialog, loading]);

  async function reload() {
    if (running.current) return;
    const action = async () => {
      operation.current?.abort();
      const controller = new AbortController(),
        id = ++generation.current;
      operation.current = controller;
      setLoading(true);
      setLoadError('');
      setFeedback('');
      clearPasswords();
      setPasswordDialog(undefined);
      initialized.current = '';
      try {
        await read(controller.signal, id);
      } catch (error) {
        if (!controller.signal.aborted && generation.current === id)
          setLoadError(safeError(error, '员工资料读取失败，请重试读取。'));
      } finally {
        if (!controller.signal.aborted && generation.current === id) setLoading(false);
      }
    };
    if (dirtyRef.current)
      setLeave(() => () => {
        void action();
      });
    else await action();
  }

  async function command(
    label: string,
    path: string,
    method: 'POST' | 'PUT' | 'PATCH',
    body: unknown,
    verify: (next: Workspace, receipt: Record<string, unknown>) => boolean,
    done?: (next: Workspace, receipt: Record<string, unknown>) => void,
    secret = '',
  ) {
    if (running.current || blocked || !dataRef.current) return;
    const id = generation.current,
      controller = new AbortController();
    running.current = true;
    operation.current = controller;
    setBusy(true);
    setFeedback('');
    let committed = false;
    try {
      const receipt = securityRecord(
        await requestWithin(api, path, { method, body: JSON.stringify(body) }, controller.signal),
      );
      committed = true;
      const next = await read(controller.signal, id);
      if (!verify(next, receipt)) {
        setLoadError(
          label.includes('重置')
            ? '尚未读回完整确认结果，请重新加载确认后再操作。'
            : label.includes('密码登录')
              ? '尚未读回已开通状态，请重新加载确认后再操作。'
              : '提交结果尚未完整确认，请重新加载确认后再操作。',
        );
        return;
      }
      done?.(next, receipt);
      setFeedback(
        `${label}已保存，并已重新读取确认。${
          label === '员工密码重置'
            ? `已注销 ${Number(receipt.sessionsRevokedCount)} 个旧会话，原业务授权不变。`
            : ''
        }`,
      );
    } catch (error) {
      if (controller.signal.aborted || generation.current !== id) return;
      if (committed || !(error instanceof ApiError) || error.status >= 500 || error.status === 409)
        setLoadError('提交结果尚未确认，请重新读取确认后再修改，避免重复提交。');
      setFeedback(
        committed
          ? '已提交，但重新读取未完成。请重新加载确认后再修改。'
          : safeError(error, `${label}保存失败，请重试。`, secret),
      );
    } finally {
      if (generation.current === id) {
        running.current = false;
        setBusy(false);
        clearPasswords();
        if (operation.current === controller) operation.current = undefined;
      }
    }
  }

  const organizationName = (id: string | null) =>
    !id
      ? '未分配部门'
      : (data?.organizations.find((item) => item.id === id)?.name ?? '归属部门待核实');
  const organizationOptions = data
    ? organizationTree(data.organizations).filter(({ organization }) =>
        organizationEffectiveActive(data.organizations, organization.id),
      )
    : [];
  const allOrganizations = data ? organizationTree(data.organizations) : [];
  const filtered = (data?.users ?? []).filter(
    (user) =>
      (!search.trim() ||
        `${user.displayName} ${user.username}`
          .toLowerCase()
          .includes(search.trim().toLowerCase())) &&
      (!statusFilter || user.status === statusFilter) &&
      (!organizationFilter ||
        (organizationFilter === '__unassigned__'
          ? !user.organizationId
          : user.organizationId === organizationFilter)),
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const pageUsers = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function openPassword(user: SecurityUser, reset: boolean) {
    if (
      blocked ||
      user.id === dataRef.current?.currentUser.id ||
      user.status !== 'ACTIVE' ||
      user.credentialReady !== reset ||
      !user.username
    )
      return;
    clearPasswords();
    setFeedback('');
    setPasswordDialog({ user, reset });
  }
  function savePassword() {
    if (!passwordDialog || blocked) return;
    const problem = passwordProblem(password, passwordConfirmation);
    if (problem) {
      setPasswordError(problem);
      return;
    }
    const user = dataRef.current?.users.find((item) => item.id === passwordDialog.user.id);
    if (
      !user ||
      user.status !== 'ACTIVE' ||
      user.id === dataRef.current?.currentUser.id ||
      user.credentialReady !== passwordDialog.reset
    ) {
      closePassword();
      return;
    }
    const reset = passwordDialog.reset;
    void command(
      reset ? '员工密码重置' : '员工密码登录',
      `${WORKSPACE}/users/${encodeURIComponent(user.id)}/${reset ? 'password-reset' : 'login-enrollment'}`,
      'POST',
      { expectedVersion: user.version, password },
      (next, receipt) => {
        const actual = next.users.find((item) => item.id === user.id);
        const version = receipt.version;
        return Boolean(
          actual?.credentialReady &&
          actual.version > user.version &&
          receipt.id === user.id &&
          typeof version === 'number' &&
          version > user.version &&
          actual.version >= version &&
          (reset
            ? receipt.reauthenticationRequired === true &&
              Number.isInteger(receipt.sessionsRevokedCount) &&
              Number(receipt.sessionsRevokedCount) >= 0
            : receipt.credentialEnrollment === 'ENROLLED'),
        );
      },
      (_next, receipt) => {
        setPasswordDialog(undefined);
        if (reset)
          setFormError(`已注销 ${Number(receipt.sessionsRevokedCount)} 个旧会话，原业务授权不变。`);
      },
      password,
    );
  }
  async function saveEmployee() {
    if (!current || editBlocked || !dirty || running.current) return;
    if (!name.trim()) {
      setFormError('请填写员工姓名。');
      return;
    }
    const user = current,
      desired = organizationId || null,
      desiredName = name.trim(),
      desiredRoles = roleIds.slice(),
      desiredAccess = cloneAccess(personalAccess);
    const organizationChanged = desired !== user.organizationId;
    const rolesChanged = !sameIds(desiredRoles, user.roleIds);
    const accessChanged = !accessEqual(desiredAccess, user.personalAccess);
    const submit = () => {
      setConfirmation(undefined);
      void command(
        '员工资料',
        `${WORKSPACE}/users/${encodeURIComponent(user.id)}`,
        'PATCH',
        {
          expectedVersion: user.version,
          displayName: desiredName,
          organizationId: desired,
          roleIds: desiredRoles,
          ...(accessChanged ? { personalAccess: desiredAccess } : {}),
          ...(organizationChanged ? { acknowledgedImpact: true } : {}),
        },
        (next, receipt) =>
          typeof receipt.version === 'number' &&
          receipt.version > user.version &&
          receipt.userId === user.id &&
          Boolean(
            next.users.some(
              (item) =>
                item.id === user.id &&
                item.version >= Number(receipt.version) &&
                item.displayName === desiredName &&
                item.organizationId === desired &&
                sameIds(item.roleIds, desiredRoles) &&
                accessEqual(item.personalAccess, desiredAccess),
            ),
          ),
        () => {
          setName(desiredName);
          initialized.current = '';
          setFormError('');
        },
      );
    };
    if (!organizationChanged) {
      if (rolesChanged || accessChanged)
        setConfirmation({
          title: '确认保存员工修改？',
          description:
            '将一起保存姓名、角色、功能权限和管理范围，并注销该用户的旧登录会话。重新登录后按新授权操作；历史订单归属保持不变。',
          action: submit,
        });
      else submit();
      return;
    }
    const id = generation.current,
      controller = new AbortController();
    running.current = true;
    operation.current = controller;
    setBusy(true);
    setFeedback('');
    try {
      const preview = securityRecord(
        await requestWithin(
          api,
          `${WORKSPACE}/users/${encodeURIComponent(user.id)}/organization-preview`,
          { method: 'POST', body: JSON.stringify({ organizationId: desired }) },
          controller.signal,
        ),
      );
      if (controller.signal.aborted || generation.current !== id) return;
      if (
        preview.userId !== user.id ||
        preview.toOrganizationId !== desired ||
        preview.expectedVersion !== user.version ||
        preview.dataWillNotBeMoved !== true ||
        !Number.isInteger(preview.employeesAffectedCount) ||
        Number(preview.employeesAffectedCount) < 0 ||
        !Number.isInteger(preview.ordersAffectedCount) ||
        Number(preview.ordersAffectedCount) < 0
      )
        throw new Error('部门变更影响尚未完整确认，请重新加载后再操作。');
      setConfirmation({
        title: '确认保存员工修改？',
        description: (
          <>
            <p>
              {organizationName(user.organizationId)} → {organizationName(desired)}
            </p>
            <p>
              姓名、部门和岗位将一起保存。变更部门会调整上下级数据访问范围并注销旧会话，不搬移历史订单。请核对所选岗位，避免扩大授权。
            </p>
            <p>
              相关员工 {Number(preview.employeesAffectedCount)} 位，相关订单{' '}
              {Number(preview.ordersAffectedCount)} 笔。请核对影响后确认。
            </p>
          </>
        ),
        action: submit,
      });
    } catch (error) {
      if (!controller.signal.aborted && generation.current === id)
        setFeedback(safeError(error, '部门影响检查失败，请重试。'));
    } finally {
      if (generation.current === id) {
        running.current = false;
        setBusy(false);
      }
    }
  }
  function changeStatus(user: SecurityUser) {
    if (blocked || user.id === data?.currentUser.id) return;
    const active = user.status === 'ACTIVE';
    setConfirmation({
      title: active ? '停用此员工？' : '重新启用此员工？',
      description: active
        ? '停用后现有登录会话失效；历史订单与审计记录保留。'
        : '启用仅恢复账号状态，不会新增岗位或扩大数据范围。',
      action: () => {
        setConfirmation(undefined);
        void command(
          '员工状态',
          `${BASE}/users/${encodeURIComponent(user.id)}/${active ? 'disable' : 'enable'}`,
          'POST',
          active ? {} : { expectedVersion: user.version },
          (next) =>
            Boolean(
              next.users.some(
                (item) => item.id === user.id && item.status === (active ? 'DISABLED' : 'ACTIVE'),
              ),
            ),
        );
      },
    });
  }
  function create(event: React.FormEvent) {
    event.preventDefault();
    if (blocked) return;
    if (!name.trim()) {
      setFormError('请填写员工姓名。');
      return;
    }
    if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]{2,99}$/.test(username.trim())) {
      setFormError(
        '登录账号需为 3–100 个字符，以英文字母或数字开头，只可使用英文字母、数字、点、下划线和短横线。',
      );
      return;
    }
    const account = username.trim(),
      displayName = name.trim(),
      org = organizationId;
    setFormError('');
    void command(
      '员工账号',
      `${WORKSPACE}/users`,
      'POST',
      { username: account, displayName, ...(org ? { organizationId: org } : {}) },
      (next, receipt) =>
        typeof receipt.id === 'string' &&
        Boolean(
          next.users.some(
            (user) =>
              user.id === receipt.id &&
              user.username.toLowerCase() === account.toLowerCase() &&
              user.displayName === displayName &&
              user.organizationId === (org || null) &&
              !user.credentialReady &&
              user.roleIds.length === 0,
          ),
        ),
      (_next, receipt) => commitView({ mode: 'edit', userId: String(receipt.id) }, true),
    );
  }

  return (
    <section className="employee-workspace" aria-label="系统用户管理">
      <header className="employee-heading">
        <div>
          <h1>
            {view.mode === 'list' ? '系统用户' : view.mode === 'new' ? '新建员工' : '编辑员工'}
          </h1>
          <p>
            {view.mode === 'list'
              ? '管理员工账号、部门归属和岗位。'
              : view.mode === 'new'
                ? '填写姓名、账号与部门，创建后继续设置权限和开通登录。'
                : '设置部门、角色、功能权限与管理范围，统一保存后生效。'}
          </p>
        </div>
        {view.mode === 'list' ? (
          <ActionButton
            type="button"
            className="employee-primary"
            disabled={blocked}
            onClick={() => navigate({ mode: 'new' })}
          >
            新建员工
          </ActionButton>
        ) : (
          <button type="button" disabled={busy} onClick={() => navigate({ mode: 'list' })}>
            返回列表
          </button>
        )}
      </header>
      {loading && <Notice>正在读取员工资料…</Notice>}
      {loadError && (
        <Notice tone="error">
          {loadError}{' '}
          <button type="button" disabled={busy || loading} onClick={() => void reload()}>
            重试读取
          </button>
        </Notice>
      )}
      {feedback && (
        <Notice tone={feedback.includes('已保存') ? 'success' : 'warning'}>{feedback}</Notice>
      )}
      {data && !canManage && <Notice>当前账号仅可查看员工，修改操作需要平台管理员权限。</Notice>}
      {view.mode === 'list' && (
        <>
          <div className="employee-filters">
            <label className="employee-search">
              <span className="employee-sr-only">查找员工</span>
              <input
                aria-label="查找员工"
                placeholder="搜索姓名或账号"
                value={search}
                disabled={loading}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </label>
            <select
              aria-label="按状态筛选员工"
              value={statusFilter}
              disabled={loading}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">全部状态</option>
              <option value="ACTIVE">正常</option>
              <option value="DISABLED">停用</option>
            </select>
            <select
              aria-label="按组织筛选员工"
              value={organizationFilter}
              disabled={loading}
              onChange={(event) => {
                setOrganizationFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">全部部门</option>
              <option value="__unassigned__">未分配部门</option>
              {allOrganizations.map(({ organization, depth }) => (
                <option value={organization.id} key={organization.id}>
                  {'　'.repeat(depth)}
                  {organization.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy || loading}
              onClick={() => {
                setSearch('');
                setStatusFilter('');
                setOrganizationFilter('');
                setPage(1);
              }}
            >
              重置筛选
            </button>
            <button type="button" disabled={busy || loading} onClick={() => void reload()}>
              刷新列表
            </button>
          </div>
          <div className="employee-table-wrap">
            <table className="employee-table">
              <thead>
                <tr>
                  <th>姓名</th>
                  <th>账号</th>
                  <th>部门</th>
                  <th>岗位</th>
                  <th>状态</th>
                  <th>登录开通</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {data &&
                  !loadError &&
                  pageUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.displayName}</strong>
                        {user.id === data.currentUser.id && <small>当前账号</small>}
                      </td>
                      <td>{user.username || '尚未设置'}</td>
                      <td>{organizationName(user.organizationId)}</td>
                      <td>
                        {user.roleIds
                          .map(
                            (id) =>
                              data.roles.find((role) => role.id === id)?.name ?? '原有岗位待核实',
                          )
                          .join('、') || '未分配岗位'}
                      </td>
                      <td>
                        <span
                          className={`employee-badge ${user.status === 'ACTIVE' ? 'is-active' : ''}`}
                        >
                          {user.status === 'ACTIVE'
                            ? '正常'
                            : user.status === 'DISABLED'
                              ? '停用'
                              : '状态待核实'}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`employee-badge ${user.credentialReady ? 'is-active' : 'is-pending'}`}
                        >
                          {user.credentialReady ? '已开通' : '待开通'}
                        </span>
                      </td>
                      <td>
                        <div className="employee-row-actions">
                          <button
                            type="button"
                            disabled={locked}
                            aria-label={`编辑${user.displayName}`}
                            onClick={() => navigate({ mode: 'edit', userId: user.id })}
                          >
                            {canManage && user.id !== data.currentUser.id ? '编辑' : '查看'}
                          </button>
                          {canManage && user.id !== data.currentUser.id && (
                            <>
                              {user.status === 'ACTIVE' && user.username && (
                                <button
                                  type="button"
                                  disabled={blocked}
                                  onClick={() => openPassword(user, user.credentialReady)}
                                >
                                  {user.credentialReady ? '重置密码' : '开通登录'}
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={blocked}
                                onClick={() => changeStatus(user)}
                              >
                                {user.status === 'ACTIVE' ? '停用' : '启用'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {(!data || loadError || filtered.length === 0) && (
                  <tr>
                    <td colSpan={7} className="employee-empty">
                      {loading
                        ? '正在加载员工…'
                        : loadError
                          ? '员工列表暂时无法读取，请重试。'
                          : data?.users.length
                            ? '没有符合筛选条件的员工。请调整或重置筛选。'
                            : '暂无员工，可点击“新建员工”创建账号。'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <footer className="employee-pagination" aria-label="员工列表分页">
            <span>
              {!loadError && data ? `共 ${filtered.length} 位员工` : '员工数量待确认'} · 每页{' '}
              {PAGE_SIZE} 条
            </span>
            <div>
              <button
                type="button"
                disabled={locked || currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                上一页
              </button>
              <span>
                第 {currentPage} / {pages} 页
              </span>
              <button
                type="button"
                disabled={locked || currentPage >= pages}
                onClick={() => setPage(currentPage + 1)}
              >
                下一页
              </button>
            </div>
          </footer>
        </>
      )}
      {view.mode === 'new' && data && (
        <form className="employee-editor" onSubmit={create}>
          <section className="employee-editor-section">
            <h2>基本资料</h2>
            <div className="employee-fields">
              <label>
                员工姓名
                <input
                  aria-label="员工姓名"
                  value={name}
                  disabled={blocked}
                  maxLength={150}
                  onChange={(event) => {
                    setName(event.target.value);
                    setFormError('');
                  }}
                />
              </label>
              <label>
                登录账号
                <input
                  aria-label="登录账号"
                  value={username}
                  disabled={blocked}
                  maxLength={100}
                  autoComplete="off"
                  onChange={(event) => {
                    setUsername(event.target.value);
                    setFormError('');
                  }}
                />
                <small>3–100 字符，字母或数字开头，可包含 . _ -</small>
              </label>
              <label>
                所属部门
                <select
                  aria-label="新员工所属组织"
                  value={organizationId}
                  disabled={blocked}
                  onChange={(event) => setOrganizationId(event.target.value)}
                >
                  <option value="">暂不分配部门</option>
                  {organizationOptions.map(({ organization, depth }) => (
                    <option value={organization.id} key={organization.id}>
                      {'　'.repeat(depth)}
                      {organization.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="employee-help">
              新建账号默认为“待开通”，不自动获得岗位或业务权限。创建后可继续分配岗位、开通登录。
            </p>
          </section>
          {formError && <Notice tone="error">{formError}</Notice>}
          <footer className="employee-editor-footer">
            <ActionButton
              type="submit"
              className="employee-primary"
              pending={busy}
              disabled={blocked}
            >
              创建员工
            </ActionButton>
            <button type="button" disabled={busy} onClick={() => navigate({ mode: 'list' })}>
              取消
            </button>
          </footer>
        </form>
      )}
      {view.mode === 'edit' && data && !current && !loading && (
        <Notice tone="warning">此员工不存在或不在可查看范围内。请返回列表确认。</Notice>
      )}
      {view.mode === 'edit' && current && data && (
        <div className="employee-editor">
          <div className="employee-editor-summary">
            <strong>{current.displayName}</strong>
            <span>{current.username || '登录账号尚未设置'}</span>
            <span className={`employee-badge ${current.status === 'ACTIVE' ? 'is-active' : ''}`}>
              {current.status === 'ACTIVE' ? '正常' : '停用'}
            </span>
          </div>
          <nav className="employee-edit-steps" aria-label="用户授权步骤">
            <a href="#employee-basic">1 用户与部门</a>
            <a href="#employee-permissions">2 功能权限</a>
            <a href={personalAccess ? '#permission-data-scope' : '#employee-permissions'}>
              3 管理范围
            </a>
            <a href="#employee-save">4 保存生效</a>
          </nav>
          {self && (
            <Notice>
              当前账号不能自行变更自己的部门或岗位授权。姓名和密码请在“个人信息”中修改。
            </Notice>
          )}
          {formError && <Notice>{formError}</Notice>}
          <section className="employee-editor-section" id="employee-basic">
            <div className="employee-section-heading">
              <h2>基本资料</h2>
            </div>
            <div className="employee-fields">
              <label>
                员工姓名
                <input
                  aria-label="员工姓名"
                  value={name}
                  disabled={editBlocked}
                  maxLength={150}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label>
                登录账号
                <input aria-label="员工登录账号" value={current.username || '尚未设置'} readOnly />
              </label>
            </div>
          </section>
          <section className="employee-editor-section">
            <div className="employee-section-heading">
              <h2>部门归属</h2>
            </div>
            <div className="employee-fields">
              <label>
                所属部门
                <select
                  aria-label="员工所属组织"
                  value={organizationId}
                  disabled={editBlocked}
                  onChange={(event) => setOrganizationId(event.target.value)}
                >
                  <option value="">暂不分配部门</option>
                  {organizationId &&
                    !organizationOptions.some(
                      ({ organization }) => organization.id === organizationId,
                    ) && (
                      <option value={organizationId} disabled>
                        {organizationName(organizationId)}（当前部门不可用）
                      </option>
                    )}
                  {organizationOptions.map(({ organization, depth }) => (
                    <option value={organization.id} key={organization.id}>
                      {'　'.repeat(depth)}
                      {organization.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="employee-help">
              更换部门前会核对数据访问影响；历史订单归属不会自动搬移。
            </p>
            {organizationId && !organizationEffectiveActive(data.organizations, organizationId) && (
              <Notice tone="warning">
                当前部门或其上级已停用、归属异常。请先选择可用部门再保存，不会自动转移归属。
              </Notice>
            )}
          </section>
          <details className="employee-editor-section employee-optional-role">
            <summary>复用岗位角色（可选） · 已选 {roleIds.length} 个</summary>
            <p className="employee-help">
              同类用户可复用角色；只配置当前用户时，使用下方个人授权，不影响其他用户。
            </p>
            <div className="employee-section-heading">
              <h2>角色分配</h2>
              <input
                type="search"
                aria-label="查找可分配角色"
                placeholder="查找角色"
                value={roleSearch}
                onChange={(event) => setRoleSearch(event.target.value)}
              />
            </div>
            <div className="employee-role-list">
              {data.roles
                .filter(
                  (role) =>
                    !roleSearch.trim() ||
                    role.name.includes(roleSearch.trim()) ||
                    role.description.includes(roleSearch.trim()) ||
                    roleIds.includes(role.id),
                )
                .map((role) => (
                  <label key={role.id}>
                    <input
                      type="checkbox"
                      aria-label={`分配${role.name}`}
                      checked={roleIds.includes(role.id)}
                      disabled={
                        editBlocked ||
                        (role.status !== 'ACTIVE' && !current.roleIds.includes(role.id))
                      }
                      onChange={(event) =>
                        setRoleIds((ids) =>
                          event.target.checked
                            ? [...ids, role.id]
                            : ids.filter((id) => id !== role.id),
                        )
                      }
                    />
                    <span>
                      <strong>{role.name}</strong>
                      {role.description && <small>{role.description}</small>}
                      {role.status !== 'ACTIVE' && <small>岗位已停用，原有配置保留</small>}
                    </span>
                  </label>
                ))}
            </div>
            {current.roleIds.filter((id) => !data.roles.some((role) => role.id === id)).length >
              0 && <p className="employee-help">另有原有岗位未在目录中展示，将保留其配置。</p>}
            {!data.roles.length && (
              <p className="employee-help">暂无可分配岗位，请先维护岗位角色。</p>
            )}
          </details>
          <section className="employee-editor-section" id="employee-permissions">
            <div className="employee-section-heading">
              <h2>此用户的功能权限与管理范围</h2>
            </div>
            <p className="employee-help">
              点击直接设置，勾选能做的功能，再选择能管理的小程序和城市。最后统一保存；此处是修改草稿。
              角色权限与个人授权共同生效，明确禁止优先。“仅用个人授权”会解除此用户的角色，不修改角色本身。
            </p>
            <div className="employee-row-actions">
              <button
                type="button"
                className={personalAccess ? undefined : 'employee-primary'}
                disabled={editBlocked}
                onClick={() => {
                  setPersonalAccess(
                    (access) => access ?? { permissions: [], businessScopeGroups: [] },
                  );
                }}
              >
                直接设置此用户的权限
              </button>
              <button
                type="button"
                disabled={editBlocked}
                onClick={() => {
                  setRoleIds([]);
                  setPersonalAccess(
                    (access) => access ?? { permissions: [], businessScopeGroups: [] },
                  );
                }}
              >
                仅用个人授权
              </button>
              {personalAccess && (
                <button
                  type="button"
                  disabled={editBlocked}
                  onClick={() => setPersonalAccess(null)}
                >
                  移除个人授权
                </button>
              )}
            </div>
            {personalAccess ? (
              <>
                <PermissionScopeEditor
                  key={current.id}
                  api={api}
                  data={data}
                  permissions={personalAccess.permissions}
                  groups={personalAccess.businessScopeGroups}
                  disabled={editBlocked}
                  onChange={(permissions, groups, completeCommonReadFields) => {
                    setPersonalAccess((access) => {
                      const fields = commonFieldsForGrantedFunctions(
                        access?.fieldPolicies ?? [],
                        completeCommonReadFields ? [] : (access?.permissions ?? []),
                        permissions,
                        data.permissions,
                        data.fieldPolicies.filter((rule) => roleIds.includes(rule.roleId)),
                      );
                      return {
                        ...access,
                        permissions,
                        businessScopeGroups: groups ?? [],
                        ...(access?.fieldPolicies || fields.length
                          ? { fieldPolicies: fields }
                          : {}),
                      };
                    });
                  }}
                />
                <p className="employee-help">
                  查看功能会补齐常用联系和金额显示；证件等信息仍受脱敏限制，原有禁止和脱敏设置保留。
                </p>
                <PermissionFieldSettings
                  rules={personalAccess.fieldPolicies ?? []}
                  catalog={data.permissions}
                  disabled={editBlocked}
                  onChange={(fieldPolicies) =>
                    setPersonalAccess((access) => (access ? { ...access, fieldPolicies } : access))
                  }
                  onComplete={() =>
                    setPersonalAccess((access) => {
                      if (!access) return access;
                      const fieldPolicies = commonFieldsForGrantedFunctions(
                        access.fieldPolicies ?? [],
                        [],
                        access.permissions,
                        data.permissions,
                        data.fieldPolicies.filter((rule) => roleIds.includes(rule.roleId)),
                      );
                      return { ...access, fieldPolicies };
                    })
                  }
                />
              </>
            ) : (
              <p className="employee-help">
                当前按所选角色授权。展开直接设置后，可单独选择功能、小程序、城市、线路和订单。
              </p>
            )}
          </section>
          <UserPermissionCheck api={api} user={current} data={data} dirty={dirty} />
          <section className="employee-editor-section">
            <div className="employee-section-heading">
              <h2>登录与账号状态</h2>
              <div className="employee-row-actions">
                {!self && (
                  <>
                    {current.status === 'ACTIVE' && current.username && (
                      <button
                        type="button"
                        disabled={blocked}
                        onClick={() => openPassword(current, current.credentialReady)}
                      >
                        {current.credentialReady ? '重置密码' : '开通登录'}
                      </button>
                    )}
                    <button type="button" disabled={blocked} onClick={() => changeStatus(current)}>
                      {current.status === 'ACTIVE' ? '停用' : '启用'}
                    </button>
                  </>
                )}
              </div>
            </div>
            <p>
              登录开通状态：<strong>{current.credentialReady ? '已开通' : '待开通'}</strong>
            </p>
            <p className="employee-help">
              开通或重置密码不新增岗位或业务权限；停用后注销该员工旧会话，历史记录保留。
            </p>
          </section>
          <footer className="employee-editor-footer" id="employee-save">
            <span>{dirty ? '有未保存修改。' : '当前资料与已读取结果一致。'}</span>
            <ActionButton
              type="button"
              className="employee-primary"
              pending={busy}
              disabled={
                editBlocked ||
                !dirty ||
                !name.trim() ||
                Boolean(
                  organizationId &&
                  !organizationEffectiveActive(data.organizations, organizationId),
                )
              }
              onClick={() => void saveEmployee()}
            >
              保存修改
            </ActionButton>
            <button type="button" disabled={busy} onClick={() => navigate({ mode: 'list' })}>
              返回列表
            </button>
            <button type="button" disabled={busy || loading} onClick={() => void reload()}>
              重新加载
            </button>
          </footer>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(passwordDialog)}
        title={
          passwordDialog?.reset
            ? `${passwordDialog.user.displayName} · 重置密码`
            : `${passwordDialog?.user.displayName ?? ''} · 初次开通登录`
        }
        description={
          <div
            className="employee-password-form"
            aria-label={passwordDialog?.reset ? '重置员工登录密码' : '初次开通员工登录'}
          >
            <p>登录账号：{passwordDialog?.user.username}</p>
            <p>
              {passwordDialog?.reset
                ? '重置会立即注销该员工全部旧登录会话，原业务授权不变。不能用此入口修改自己的密码。'
                : '开通仅启用密码登录，不新增业务权限。请通过安全渠道通知员工。'}
            </p>
            <label>
              {passwordDialog?.reset ? '新密码' : '初始密码'}
              <input
                type="password"
                autoComplete="new-password"
                autoFocus
                maxLength={128}
                aria-label={passwordDialog?.reset ? '重置员工新密码' : '初始登录密码'}
                value={password}
                disabled={blocked}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setPasswordError('');
                }}
              />
            </label>
            <label>
              再次确认密码
              <input
                type="password"
                autoComplete="new-password"
                maxLength={128}
                aria-label={passwordDialog?.reset ? '再次确认重置密码' : '确认初始登录密码'}
                value={passwordConfirmation}
                disabled={blocked}
                onChange={(event) => {
                  setPasswordConfirmation(event.target.value);
                  setPasswordError('');
                }}
              />
            </label>
            <small>
              12–128 个字符，包含英文大写字母、小写字母和数字，可使用符号。密码不会保存于浏览器。
            </small>
            {(passwordError || loadError || feedback) && (
              <Notice tone="error">{passwordError || loadError || feedback}</Notice>
            )}
          </div>
        }
        confirmLabel={passwordDialog?.reset ? '确认重置密码' : '确认开通登录'}
        pending={busy}
        confirmDisabled={blocked}
        dangerous={Boolean(passwordDialog?.reset)}
        onConfirm={savePassword}
        onCancel={closePassword}
      />
      <ConfirmDialog
        open={Boolean(confirmation)}
        title={confirmation?.title ?? ''}
        description={confirmation?.description}
        pending={busy}
        confirmDisabled={blocked}
        onConfirm={() => confirmation?.action()}
        onCancel={() => {
          if (!running.current) setConfirmation(undefined);
        }}
      />
      <ConfirmDialog
        open={Boolean(leave)}
        title="放弃未保存的修改？"
        description="离开后未保存的员工资料和岗位草稿将丢失，已成功保存的资料不受影响。"
        confirmLabel="放弃修改并继续"
        onConfirm={() => {
          const action = leave;
          setLeave(undefined);
          action?.();
        }}
        onCancel={() => setLeave(undefined)}
      />
    </section>
  );
}
